<template>
  <div v-if="disasterNo">
    <!-- 共通ヘッダー（タイトル/サブタイトル） -->
    <div class="content-header">
      <h1 class="page-title">
        <i class="fas fa-check-circle"></i>
        代替避難所の確定
      </h1>
    </div>
    <error-message :error-object="errorObject" />
    <success-message :success-object="successObject" />
    <!-- ステップインジケーター -->
    <div class="step-indicator">
      <div
        class="step"
        :class="{ active: currentStep === 1 }"
        @click="goToStep(1)"
      >
        <div class="step-number">1</div>
        <span>受入先市町の受入可否の確認</span>
      </div>
      <div class="step-connector"></div>
      <div
        class="step"
        :class="{ active: currentStep === 2 }"
        @click="goToStep(2)"
      >
        <div class="step-number">2</div>
        <span>避難元市町の代替避難先の確認</span>
      </div>
      <div v-if="this.isAuth" class="step-connector"></div>
      <div
        v-if="this.isAuth"
        class="step"
        :class="{ active: currentStep === 3 }"
        @click="goToStep(3)"
      >
        <div class="step-number">3</div>
        <span>県の確認</span>
      </div>
    </div>

    <!-- ステップ1: 受入先市町の受入可否の確認 -->
    <div>
      <section>
        <!-- 一覧部 -->
        <div class="content-card">
          <div
            style="display:flex; justify-content:space-between; align-items:center; margin-bottom:16px; flex-wrap:wrap;"
          >
            <div
              style="display:flex; align-items:center; gap:16px; flex-wrap:wrap;"
          >
              <h2 class="card-title" style="margin:0;">
                避難元／避難先の確定一覧
              </h2>
              <select
                v-if="currentStep === 2 || currentStep === 3"
                v-model="evaCityFilter"
                style="padding:8px 12px; border:1px solid #c7c7c7; border-radius:4px; font-size:14px; min-width:150px;"
              >
                <option value="">避難元の市町を絞込</option>
                <option
                  v-for="city in uniqueCitiesEva"
                  :key="city"
                  :value="city"
                  >{{ city }}</option
                >
              </select>
              <select
                v-if="currentStep === 1 || currentStep === 3"
                v-model="tempCityFilter"
                style="padding:8px 12px; border:1px solid #c7c7c7; border-radius:4px; font-size:14px; min-width:150px;"
              >
                <option value="">受入先の市町を絞込</option>
                <option
                  v-for="city in uniqueCitiesTemporary"
                  :key="city"
                  :value="city"
                  >{{ city }}</option
                >
              </select>
              <div v-if="currentStep === 1" style="font-size:14px;">
                ※代替の避難所にて、避難元の自治会の受入が可能な場合は、「<label
                  style="color:red;"
                  >受入可能</label
                >」にチェックし「確定」を押下してください。
              </div>
              <div v-if="currentStep === 2" style="font-size:14px;">
                ※代替避難所への避難が可能な自治会の場合は、「<label
                  style="color:red;"
                  >避難可能</label
                >」にチェックし「確定」を押下してください。
              </div>
              <div
                v-if="currentStep === 3 && !showDoneOnly"
                style="font-size:12px;"
              >
                ※「<label style="color:red;">避難可能</label>」と「<label
                  style="color:red;"
                  >受入可能</label
                >」を確認して、問題なければ「確定」を押下してください。<br />
                ※「<label style="color:red;">受入可能</label>」と「<label
                  style="color:red;"
                  >避難可能</label
                >」の両方にチェックされているものが「確定」として登録されます。<br />
                <label style="padding-left: 15px;"
                  >どちらか一方でもチェックがない場合は、代替避難所の仮登録画面で再度代替避難先を仮登録してください。</label
                >
              </div>
              <div
                v-if="currentStep === 3 && showDoneOnly"
                style="font-size:12px;"
              >
                ※代替避難先の確定を取り消す場合、「<label style="color:red;"
                  >取消選択</label
                >」にチェックして「確定取消」を押下してください。
              </div>
            </div>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                v-if="currentStep === 3"
                class="btn-secondary"
                @click="toggleDoneFilter"
              >
                {{ showDoneOnly ? "未確定情報のみ表示" : "確定情報のみ表示" }}
              </v-btn>
              <v-btn class="btn-primary" @click="exportExcel()">
                Excel出力
              </v-btn>
              <v-btn
                v-if="isAuth && currentStep === 3"
                class="btn-primary"
                @click="confirm"
              >
                {{ showDoneOnly ? "確定取消" : "確定" }}
              </v-btn>
              <v-btn
                v-if="!isAuth && currentStep !== 3"
                class="btn-primary"
                @click="confirm"
              >
                確定
              </v-btn>
            </v-card-actions>
          </div>
          <div
            class
            :style="{ height: tableHeight() + 'px', overflow: 'auto' }"
          >
            <table class="table adjustment-table">
              <thead>
                <tr>
                  <th
                    v-if="showDoneOnly && currentStep == 3"
                    rowspan="3"
                    style="color:red;"
                  >
                    取消<br />選択
                  </th>
                  <th colspan="4">避難元</th>
                  <th
                    v-if="currentStep === 2 || currentStep === 3"
                    rowspan="3"
                    :style="{
                      color: showDoneOnly && currentStep == 3 ? '#555' : 'red'
                    }"
                  >
                    避難<br />可能
                  </th>
                  <th
                    v-if="currentStep === 1 || currentStep === 3"
                    rowspan="3"
                    :style="{
                      color: showDoneOnly && currentStep == 3 ? '#555' : 'red'
                    }"
                  >
                    受入<br />可能
                  </th>
                  <th colspan="9">避難先</th>
                </tr>
                <tr>
                  <th
                    rowspan="2"
                    style="cursor:pointer;"
                    @click="sortBy('eva_municip_name')"
                  >
                    市町名
                    <span v-if="sortKey === 'eva_municip_name'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                  <th
                    rowspan="2"
                    style="cursor:pointer;"
                    @click="sortBy('eva_autonomy_name')"
                  >
                    自治会名
                    <span v-if="sortKey === 'eva_autonomy_name'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                  <th rowspan="2">人口</th>
                  <th
                    rowspan="2"
                    style="cursor:pointer;"
                    @click="sortBy('eva_fa_name')"
                  >
                    使用困難な避難所
                    <span v-if="sortKey === 'eva_fa_name'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                  <th
                    rowspan="2"
                    style="cursor:pointer;"
                    @click="sortBy('temp_municip_name')"
                  >
                    受入先市町名
                    <span v-if="sortKey === 'temp_municip_name'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                  <th
                    rowspan="2"
                    style="cursor:pointer;"
                    @click="sortBy('temp_fa_name')"
                  >
                    代替の避難所
                    <span v-if="sortKey === 'temp_fa_name'">
                      <i
                        :class="[
                          'fas',
                          sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                        ]"
                      ></i>
                    </span>
                    <span v-else>
                      <i class="fas fa-sort"></i>
                    </span>
                  </th>
                  <th colspan="2">原発からの</th>
                  <th rowspan="2">収容人数</th>
                  <th colspan="2">避難所間の</th>
                  <th rowspan="2">収容人数<br />（想定数）</th>
                </tr>
                <tr>
                  <th>方角</th>
                  <th>直線距離</th>
                  <th>方角</th>
                  <th>直線距離</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="data in sortedTemporaries" :key="data.key">
                  <td
                    v-if="showDoneOnly && currentStep == 3"
                    style="text-align: center;"
                  >
                    <input type="checkbox" v-model="data.cancel_flg" />
                  </td>
                  <td style="text-align: left;">{{ data.eva_municip_name }}</td>
                  <td style="text-align: left;">
                    {{ data.eva_autonomy_name }}
                  </td>
                  <td style="text-align: right;">{{ data.eva_population }}</td>
                  <td style="text-align: left;">{{ data.eva_fa_name }}</td>
                  <td v-if="currentStep !== 1" style="text-align: center;">
                    <input
                      type="checkbox"
                      v-model="data.eva_city_flg"
                      :disabled="
                        (!isAuth && data.pre_flg) ||
                          (isAuth && currentStep !== 3) ||
                          (showDoneOnly && currentStep == 3)
                      "
                    />
                  </td>
                  <td v-if="currentStep !== 2" style="text-align: center;">
                    <input
                      type="checkbox"
                      v-model="data.host_city_flg"
                      :disabled="
                        (!isAuth && data.pre_flg) ||
                          (isAuth && currentStep !== 3) ||
                          (showDoneOnly && currentStep == 3)
                      "
                    />
                  </td>
                  <td style="text-align: left;">
                    {{ data.temp_municip_name }}
                  </td>
                  <td style="text-align: left;">{{ data.temp_fa_name }}</td>
                  <td style="text-align: center;">
                    {{ data.temp_direction_from_npp_name }}
                  </td>
                  <td style="text-align: right;">
                    {{ data.temp_distance_from_npp }} km
                  </td>
                  <td style="text-align: right;">
                    {{ data.temp_max_capacity }}
                  </td>
                  <td style="text-align: center;">
                    {{ data.direction_from_eva_name }}
                  </td>
                  <td style="text-align: right;">
                    {{ data.distance_from_eva }} km
                  </td>
                  <td style="text-align: right;">
                    {{ data.expected_capacity }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
    <ConfirmDialog ref="confirmDialog" />
    <ErrorDialog ref="errorDialog" />
    <v-overlay :value="overlay" opacity="1" color="white">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import ExcelJS from "exceljs";
import messageUtils from "@/mixins/message-utils";
import { ErrorTodoMessages } from "@/js/constants/error-messages";
import { RepositoryFactory } from "@/js/repository/RepositoryFactory";
import { ScreenId } from "@/js/value-objects/screen-id";

const adjustmentSystemRepository = RepositoryFactory.get("adjustmentSystem");
const confirmationEvaRepository = RepositoryFactory.get("confirmationEva");

const LIST_MAX_HEIGHT = 560;

export default {
  name: "ConfirmationEva",
  mixins: [messageUtils],
  components: {
    ErrorMessage: () => import("@/components/ErrorMessage"),
    SuccessMessage: () => import("@/components/SuccessMessage"),
    ErrorDialog: () => import("@/components/adjustment/AdErrorDialog"),
    ConfirmDialog: () => import("@/components/adjustment/AdConfirmDialog")
  },
  props: {
    disasterNo: {
      type: String,
      default: ""
    },
    receivedStep: {
      type: Number,
      default: 1
    }
  },
  watch: {
    receivedStep: {
      immediate: true,
      handler(newVal) {
        this.currentStep = newVal;
      }
    }
  },
  data: () => ({
    currentStep: 1,
    overlay: false,
    isAuth: false,
    isInitError: false,
    loginMunicipCode: "",
    screenTitle: "",
    screenId: "",
    readOnly: false,
    tempCityFilter: "",
    evaCityFilter: "",
    showDoneOnly: false,
    sortKey: "",
    sortOrder: "asc",
    temporaryList: []
  }),

  computed: {
    // 受入先の絞込み
    filteredTemporaries() {
      return this.temporaryList.filter(temporary => {
        return this.isValidTemporary(temporary);
      });
    },
    // 避難元の絞込み
    filteredEva() {
      return this.temporaryList.filter(temporary => {
        return this.isValidEva(temporary);
      });
    },
    // 県の絞込み
    filteredPre() {
      return this.temporaryList.filter(temporary => {
        return this.isValidPre(temporary);
      });
    },
    // 一覧のソート
    sortedTemporaries() {
      let filtered = [];
      if (this.currentStep === 1) {
        filtered = this.filteredTemporaries;
      } else if (this.currentStep === 2) {
        filtered = this.filteredEva;
      } else {
        filtered = this.filteredPre;
      }
      if (!this.sortKey) return filtered;

      return [...filtered].sort((a, b) => {
        let aVal = a[this.sortKey];
        let bVal = b[this.sortKey];

        const modifier = this.sortOrder === "asc" ? 1 : -1;

        if (aVal < bVal) return -1 * modifier;
        if (aVal > bVal) return 1 * modifier;
        return 0;
      });
    },
    // 一覧の市町名絞込みリスト作成
    uniqueCitiesTemporary() {
      const filteredList = this.temporaryList.filter(temporary => {
        if (
          !this.isAuth &&
          this.loginMunicipCode &&
          this.loginMunicipCode !== temporary.temp_municip_code
        )
          return false;
        return true;
      });
      const cities = [
        ...new Set(filteredList.map(temporary => temporary.temp_municip_name))
      ];
      return cities.filter(city => city).sort();
    },
    // 一覧の市町名絞込みリスト作成
    uniqueCitiesEva() {
      const filteredList = this.temporaryList.filter(temporary => {
        if (
          !this.isAuth &&
          this.loginMunicipCode &&
          this.loginMunicipCode !== temporary.eva_municip_code
        )
          return false;

        if (this.currentStep === 2 && !temporary.host_city_flg) return false;

        return true;
      });
      const cities = [
        ...new Set(filteredList.map(temporary => temporary.eva_municip_name))
      ];
      return cities.filter(city => city).sort();
    }
  },

  async mounted() {
    await this.initialize();
    if (this.disasterNo === "" || this.isInitError) return;
    await this.getList();
  },

  methods: {
    // 初期処理
    async initialize() {
      this.clearMessage();
      this.overlay = true;
      try {
        this.clearSort();
        /* 権限取得 */
        const payload = {
          disasterNo: this.disasterNo
        };
        await adjustmentSystemRepository
          .list(ScreenId.SF4101, payload)
          .then(response => {
            this.isInitError = false;
            this.isAuth = response.data.isAuth;
          })
          .catch(e => {
            this.isInitError = true;
            this.handleUpdateError(e);
          });
      } catch (error) {
        this.isInitError = true;
        this.handleUpdateError(error);
        console.error("ユーザーの権限取得に失敗しました:", error);
      } finally {
        this.overlay = false;
      }
    },
    // 検索条件表示非表示の時の一覧の高さ調整
    tableHeight() {
      return LIST_MAX_HEIGHT;
    },
    // ページ遷移
    async goToStep(step) {
      // 初期化エラー時は処理しない
      if (this.isInitError) return;

      this.tempCityFilter = "";
      this.evaCityFilter = "";
      this.clearMessage();
      if (step === 3) this.showDoneOnly = false;
      this.currentStep = step;
      this.$emit("change-step", step);
    },
    // 一覧のソート処理
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortOrder = "asc";
      }
    },
    clearSort() {
      this.sortKey = "";
      this.sortOrder = "asc";
    },
    isValidTemporary(data) {
      if (
        !this.isAuth &&
        this.loginMunicipCode &&
        this.loginMunicipCode !== data.temp_municip_code
      )
        return false;
      if (this.tempCityFilter && data.temp_municip_name !== this.tempCityFilter)
        return false;
      return true;
    },
    isValidEva(data) {
      if (
        !this.isAuth &&
        this.loginMunicipCode &&
        this.loginMunicipCode !== data.eva_municip_code
      )
        return false;
      if (!data.host_city_flg) return false;
      if (this.evaCityFilter && data.eva_municip_name !== this.evaCityFilter)
        return false;
      return true;
    },
    isValidPre(data) {
      if (this.tempCityFilter && data.temp_municip_name !== this.tempCityFilter)
        return false;

      if (this.evaCityFilter && data.eva_municip_name !== this.evaCityFilter)
        return false;

      if (this.showDoneOnly && !data.pre_flg) return false;
      if (!this.showDoneOnly && data.pre_flg) return false;

      return true;
    },
    // 仮登録データの表示切り替え
    toggleDoneFilter() {
      this.clearMessage();
      this.showDoneOnly = !this.showDoneOnly;
    },
    // 一覧の取得
    async getList() {
      this.overlay = true;
      try {
        this.clearSort();
        const payload = {
          disasterNo: this.disasterNo
        };

        /* 代替避難先の仮登録一覧取得 */
        await confirmationEvaRepository
          .list(ScreenId.SF4101, payload)
          .then(response => {
            this.temporaryList = response.data.results;
            this.loginMunicipCode = response.data.loginMunicipCode;
          })
          .catch(e => {
            this.handleUpdateError(e);
          });
      } catch (error) {
        this.handleUpdateError(error);
        console.error("代替避難先の仮登録一覧の取得に失敗しました:", error);
      } finally {
        this.overlay = false;
      }
    },
    // EXCEL出力
    async exportExcel() {
      // 初期化エラー時は処理しない
      if (this.isInitError) return;

      let templateFileName;
      let downloadFileName;
      let dateCell;

      this.overlay = true;
      try {
        if (!this.temporaryList.length > 0) {
          await this.$refs.errorDialog.open(ErrorTodoMessages.ERWE48001);
          return;
        }

        if (this.currentStep == 1) {
          templateFileName = "confirmation_eva_list_1.xlsx";
          downloadFileName = "避難元避難先確定一覧（受入先）";
          dateCell = "N2";
        } else if (this.currentStep == 2) {
          templateFileName = "confirmation_eva_list_2.xlsx";
          downloadFileName = "避難元避難先確定一覧（避難元）";
          dateCell = "N2";
        } else {
          templateFileName = "confirmation_eva_list_3.xlsx";
          downloadFileName = "避難元避難先確定一覧（県）";
          dateCell = "O2";
        }

        /* タイムスタンプ算出 */
        let now = new Date();
        let year = now.getFullYear();
        let month = ("00" + (now.getMonth() + 1)).slice(-2);
        let day = ("00" + now.getDate()).slice(-2);
        let hour = ("00" + now.getHours()).slice(-2);
        let minute = ("00" + now.getMinutes()).slice(-2);
        let timeStamp = year + month + day + hour + minute;
        let timeStamp2 =
          year + "/" + month + "/" + day + " " + hour + ":" + minute;

        // 固定のExcelファイルを取得（publicフォルダに配置されていると仮定）
        const response = await fetch("/template/" + templateFileName);
        const arrayBuffer = await response.arrayBuffer();

        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);
        const worksheet = workbook.getWorksheet(1); // 最初のシートを取得

        // 罫線スタイルを定義
        const borderStyle = {
          style: "thin",
          color: { argb: "FF000000" }
        };

        //5行目まではヘッダー
        var rowNo = 5;
        this.temporaryList.forEach(data => {
          if (this.currentStep === 1) {
            if (!this.isValidTemporary(data)) return;
          } else if (this.currentStep === 2) {
            if (!this.isValidEva(data)) return;
          } else {
            if (!this.isValidPre(data)) return;
          }

          rowNo++;
          let rowData = [];
          if (this.currentStep === 1) {
            rowData = [
              rowNo - 5,
              data.eva_municip_name,
              data.eva_autonomy_name,
              Number(data.eva_population),
              data.eva_fa_name,
              data.host_city_flg_db ? "○" : "",
              data.temp_municip_name,
              data.temp_fa_name,
              data.temp_direction_from_npp_name,
              data.temp_distance_from_npp + " km",
              Number(data.temp_max_capacity),
              data.direction_from_eva_name,
              data.distance_from_eva + " km",
              Number(data.expected_capacity)
            ];
          } else if (this.currentStep === 2) {
            rowData = [
              rowNo - 5,
              data.eva_municip_name,
              data.eva_autonomy_name,
              Number(data.eva_population),
              data.eva_fa_name,
              data.eva_city_flg_db ? "○" : "",
              data.temp_municip_name,
              data.temp_fa_name,
              data.temp_direction_from_npp_name,
              data.temp_distance_from_npp + " km",
              Number(data.temp_max_capacity),
              data.direction_from_eva_name,
              data.distance_from_eva + " km",
              Number(data.expected_capacity)
            ];
          } else {
            rowData = [
              rowNo - 5,
              data.eva_municip_name,
              data.eva_autonomy_name,
              Number(data.eva_population),
              data.eva_fa_name,
              data.eva_city_flg_db ? "○" : "",
              data.host_city_flg_db ? "○" : "",
              data.temp_municip_name,
              data.temp_fa_name,
              data.temp_direction_from_npp_name,
              data.temp_distance_from_npp + " km",
              Number(data.temp_max_capacity),
              data.direction_from_eva_name,
              data.distance_from_eva + " km",
              Number(data.expected_capacity)
            ];
          }

          const row = worksheet.insertRow(rowNo, rowData);

          // 各セルに罫線を設定
          row.eachCell(cell => {
            cell.border = {
              top: borderStyle,
              left: borderStyle,
              bottom: borderStyle,
              right: borderStyle
            };
          });
        });

        const cell = worksheet.getCell(dateCell);
        cell.value = "出力日時：" + timeStamp2;

        // 編集済みファイルをダウンロード
        const editedBuffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([editedBuffer], {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = downloadFileName + "_" + timeStamp + ".xlsx";
        link.click();
      } catch (error) {
        console.error("EXCEL出力に失敗しました:", error);
      } finally {
        this.overlay = false;
      }
    },
    // 仮登録処理
    async confirm() {
      // 初期化エラー時は処理しない
      if (this.isInitError) return;

      let msg = "";
      try {
        if (!this.temporaryList.length > 0) {
          await this.$refs.errorDialog.open(ErrorTodoMessages.ERWE48001);
          return;
        }

        let btnText = "確定";
        if (this.currentStep === 1) {
          msg = "一覧の内容で受入可否を確定します。\nよろしいですか？";
        } else if (this.currentStep === 2) {
          msg = "一覧の内容で避難可否を確定します。\nよろしいですか？";
        } else {
          msg = this.showDoneOnly
            ? "確定した代替避難所を取り消しします。\nよろしいですか？"
            : "一覧の内容で受入可否、避難可否を確定します。\nよろしいですか？";
          btnText = this.showDoneOnly ? "確定取消" : "確定";
        }

        // 更新確認ダイアログを表示
        const result = await this.$refs.confirmDialog.open(msg, btnText);
        if (!result) return;

        this.clearMessage();
        this.overlay = true;

        const paramList = [];
        this.temporaryList.forEach(data => {
          if (!this.isAuth) {
            if (data.pre_flg) return;
          }
          if (this.currentStep === 1) {
            if (!this.isValidTemporary(data)) return;
          } else if (this.currentStep === 2) {
            if (!this.isValidEva(data)) return;
          } else {
            if (!this.isValidPre(data)) return;
            if (this.showDoneOnly && !data.cancel_flg) return;
          }

          paramList.push({
            hostCityFlg: data.host_city_flg || false,
            evaCityFlg: data.eva_city_flg || false,
            preFlg: data.pre_flg,
            cancelFlg: data.cancel_flg,
            autonomyCode: data.eva_autonomy_code,
            evaFaId: data.eva_fa_id,
            tempFaId: data.temp_fa_id
          });
        });

        if (!paramList.length > 0) {
          await this.$refs.errorDialog.open(ErrorTodoMessages.ERWE48004);
          return;
        }

        const payload = {
          disasterNo: this.disasterNo,
          stepId: this.currentStep,
          list: paramList
        };

        let isSuccess = false;
        await confirmationEvaRepository
          .update(ScreenId.SF4101, payload)
          .then(response => {
            isSuccess = true;
            this.handleSuccess(response);
            if (this.currentStep !== 3 || !this.showDoneOnly) {
              this.$emit("update-status", {
                menu: "confirmation_eva_" + String(this.currentStep),
                completed: true
              });
            }
          })
          .catch(error => {
            this.handleUpdateError(error);
          });

        if (isSuccess) {
          // 登録後の最新データを取得する
          await this.getList();
        }
      } catch (error) {
        this.handleUpdateError(error);
        console.error("代替避難所の確定に失敗しました:", error);
      } finally {
        this.overlay = false;
      }
    }
  }
};
</script>
